import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { useDebouncedIngredientSearch } from '@/hooks/useDebouncedIngredientSearch';
import { useSpoonacularTagEnrichment } from '@/hooks/useSpoonacularTagEnrichment';
import { getIngredientNutrition, getIngredientNutritionByIngredientId, getIngredientsAutocomplete } from '@/lib/apiService';
import { Ingredient } from '@/components/recipes/IngredientList';
import { gramsToMl, isGramUnit, mlToGrams, roundTo2, toPositiveNumber } from '@/utils/gram-ml';
import {
  extractIngredientUnitMetadata,
  normalizeIngredientUnitKey,
  normalizeIngredientUnits,
  resolveIngredientAmountMl,
  resolveIngredientUnitMlWeight,
} from '@/utils/ingredient-units';
import { mergeRecipeAllergens, mergeRecipeIntolerances } from '@/utils/recipe-allergens';

const scaleNutritionForAmount = (
  nutrition: Record<string, number> | null,
  ratio: number,
): Record<string, number> | null => {
  if (!nutrition || !Number.isFinite(ratio) || ratio <= 0) return nutrition;
  const scaled: Record<string, number> = {};
  for (const [key, value] of Object.entries(nutrition)) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) continue;
    scaled[key] = roundTo2(numeric * ratio);
  }
  return scaled;
};

const nutritionForAmountToNutritionData = (
  nutritionForAmount: Record<string, number> | null,
): Ingredient['nutritionData'] | null => {
  if (!nutritionForAmount) return null;

  return {
    calories: Number(nutritionForAmount.calories) || 0,
    macros: {
      protein: Number(nutritionForAmount.protein) || 0,
      carbs: Number(nutritionForAmount.carbohydrate) || 0,
      fat: Number(nutritionForAmount.total_fat) || 0,
      fiber: Number(nutritionForAmount.dietary_fibre) || 0,
      saturatedFat: Number(nutritionForAmount.saturated_fat) || 0,
      alcohol: Number((nutritionForAmount as Record<string, unknown>).alcohol ?? (nutritionForAmount as Record<string, unknown>).ethyl_alcohol) || 0,
    },
  };
};

const getPositiveFactorOrNull = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const resolveAmountInGrams = (input: {
  amount: number | null;
  unit: string;
  unitWeightOverrideGrams: number | null;
  unitWeightOverrideMl: number | null;
  gramsPerMl: number | null;
  baseAmountInGrams: number | null;
  unitWeightsByKey: Record<string, number>;
}): number | null => {
  const {
    amount,
    unit,
    unitWeightOverrideGrams,
    unitWeightOverrideMl,
    gramsPerMl: effectiveGramsPerMl,
    baseAmountInGrams,
    unitWeightsByKey,
  } = input;
  if (!amount || amount <= 0) return null;

  if (isGramUnit(unit)) {
    return roundTo2(amount);
  }

  if (unitWeightOverrideGrams && unitWeightOverrideGrams > 0) {
    return roundTo2(amount * unitWeightOverrideGrams);
  }

  if (unitWeightOverrideMl && unitWeightOverrideMl > 0) {
    const gramsFromMl = mlToGrams(unitWeightOverrideMl, effectiveGramsPerMl);
    if (gramsFromMl != null) {
      return roundTo2(amount * gramsFromMl);
    }
  }

  if (baseAmountInGrams && baseAmountInGrams > 0) {
    return roundTo2(baseAmountInGrams);
  }

  const standardUnitWeight = toPositiveNumber(unitWeightsByKey[normalizeIngredientUnitKey(unit)]);
  if (standardUnitWeight) {
    return roundTo2(amount * standardUnitWeight);
  }

  return null;
};

const resolveUnitWeightFromIngredient = (ingredient: Ingredient): number | null => {
  const explicit = toPositiveNumber(ingredient.unitWeightGrams);
  if (explicit) return explicit;

  const amount = toPositiveNumber(ingredient.amount);
  const amountGrams = toPositiveNumber(ingredient.amountGrams);
  if (!amount || !amountGrams || isGramUnit(ingredient.unit)) return null;

  return roundTo2(amountGrams / amount);
};

const resolveUnitWeightMlFromIngredient = (ingredient: Ingredient, gramsPerMl: number | null): number | null => {
  const explicit = toPositiveNumber(ingredient.unitWeightMl);
  if (explicit) return explicit;

  const grams = resolveUnitWeightFromIngredient(ingredient);
  return gramsToMl(grams, gramsPerMl);
};

export const useIngredientManagement = (
  options?: { searchSource?: 'all' | 'spoonacular' | 'edamam' | 'internal' }
) => {
  const searchSource = options?.searchSource ?? 'spoonacular';
  const lookupSource: 'all' | 'spoonacular' | 'internal' =
    searchSource === 'internal' ? 'internal' : searchSource === 'spoonacular' ? 'all' : 'all';

  // Ingredient list state
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  // Ingredient form state
  const [isAddingIngredient, setIsAddingIngredient] = useState(false);
  const [ingredientSearchQuery, setIngredientSearchQueryState] = useState('');
  const [selectedIngredientName, setSelectedIngredientName] = useState('');
  const [selectedIngredientLookupId, setSelectedIngredientLookupId] = useState('');
  const [ingredientAmount, setIngredientAmount] = useState('');
  const [ingredientUnit, setIngredientUnit] = useState('');
  const [unitWeightGrams, setUnitWeightGrams] = useState('');
  const [unitWeightMl, setUnitWeightMl] = useState('');
  const [showIngredientResults, setShowIngredientResults] = useState(false);
  const [baseNutritionData, setBaseNutritionData] = useState<Ingredient['nutritionData'] | null>(null);
  const [baseNutritionForAmount, setBaseNutritionForAmount] = useState<Record<string, number> | null>(null);
  const [baseAmountInGrams, setBaseAmountInGrams] = useState<number | null>(null);
  const [selectedYieldFactor, setSelectedYieldFactor] = useState<number | null>(null);
  const [selectedRetentionFactor, setSelectedRetentionFactor] = useState<number | null>(null);
  const [gramsPerMl, setGramsPerMl] = useState<number | null>(null);
  const [isLoadingNutrition, setIsLoadingNutrition] = useState(false);
  const [bulkIngredientInput, setBulkIngredientInput] = useState('');
  const [isBulkAddingIngredients, setIsBulkAddingIngredients] = useState(false);
  const [availableUnits, setAvailableUnits] = useState<string[]>([]);
  const [unitLabelsByKey, setUnitLabelsByKey] = useState<Record<string, string>>({});
  const [unitWeightsByKey, setUnitWeightsByKey] = useState<Record<string, number>>({});
  const [unitMlWeightsByKey, setUnitMlWeightsByKey] = useState<Record<string, number>>({});
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
  const [isIngredientSelectionLocked, setIsIngredientSelectionLocked] = useState(false);
  /** Thumbnail URL from last search selection; persisted on ingredient when added. */
  const selectedIngredientImageUrlRef = useRef<string | undefined>(undefined);
  const selectedIngredientAllergensRef = useRef<string[]>([]);
  const selectedIngredientIntolerancesRef = useRef<string[]>([]);
  /** Exposed for UI (e.g. modal details card); kept in sync with ref on select/edit/reset. */
  const [selectedIngredientPreviewUrl, setSelectedIngredientPreviewUrl] = useState<string | undefined>();

  const parsedIngredientAmount = useMemo(() => toPositiveNumber(ingredientAmount), [ingredientAmount]);
  const parsedUnitWeightOverrideGrams = useMemo(() => toPositiveNumber(unitWeightGrams), [unitWeightGrams]);
  const parsedUnitWeightOverrideMl = useMemo(() => toPositiveNumber(unitWeightMl), [unitWeightMl]);
  const effectiveGramsPerMl = gramsPerMl;

  const effectiveAmountGrams = useMemo(() => resolveAmountInGrams({
    amount: parsedIngredientAmount,
    unit: ingredientUnit,
    unitWeightOverrideGrams: parsedUnitWeightOverrideGrams,
    unitWeightOverrideMl: parsedUnitWeightOverrideMl,
    gramsPerMl: effectiveGramsPerMl,
    baseAmountInGrams,
    unitWeightsByKey,
  }), [
    baseAmountInGrams,
    effectiveGramsPerMl,
    ingredientUnit,
    parsedIngredientAmount,
    parsedUnitWeightOverrideGrams,
    parsedUnitWeightOverrideMl,
    unitWeightsByKey,
  ]);

  const effectiveAmountMl = useMemo(() => resolveIngredientAmountMl({
    amount: parsedIngredientAmount,
    unit: ingredientUnit,
    unitWeightMl: parsedUnitWeightOverrideMl,
    unitWeightGrams: parsedUnitWeightOverrideGrams,
    gramsPerMl: effectiveGramsPerMl,
    mlWeightsByKey: unitMlWeightsByKey,
    amountGrams: effectiveAmountGrams,
  }), [
    effectiveAmountGrams,
    effectiveGramsPerMl,
    ingredientUnit,
    parsedIngredientAmount,
    parsedUnitWeightOverrideGrams,
    parsedUnitWeightOverrideMl,
    unitMlWeightsByKey,
  ]);

  const effectiveNutritionForAmount = useMemo(() => {
    if (!baseNutritionForAmount) return null;

    if (
      !baseAmountInGrams
      || baseAmountInGrams <= 0
      || !effectiveAmountGrams
      || effectiveAmountGrams <= 0
    ) {
      return baseNutritionForAmount;
    }

    const ratio = effectiveAmountGrams / baseAmountInGrams;
    return scaleNutritionForAmount(baseNutritionForAmount, ratio);
  }, [baseAmountInGrams, baseNutritionForAmount, effectiveAmountGrams]);

  const effectiveNutritionData = useMemo(() => {
    const mapped = nutritionForAmountToNutritionData(effectiveNutritionForAmount);
    return mapped ?? baseNutritionData;
  }, [baseNutritionData, effectiveNutritionForAmount]);

  // Refs for tracking
  const ingredientDropdownManuallyClosedRef = useRef(false);
  const lastFetchedRef = useRef<{ ingredientId: string; ingredient: string; amount: number; unit: string } | null>(null);
  const ingredientRequestIdRef = useRef(0);
  const normalizedSearchQuery = ingredientSearchQuery.trim().toLowerCase();
  const normalizedSelectedIngredient = selectedIngredientName.trim().toLowerCase();
  const isSearchQuerySelectedIngredient =
    Boolean(normalizedSelectedIngredient) && normalizedSearchQuery === normalizedSelectedIngredient;
  const shouldEnableIngredientSearch = !isIngredientSelectionLocked && !isSearchQuerySelectedIngredient;

  // Use unified ingredient search hook
  const { formattedResults: rawIngredientSearchResults, loading: isSearchingIngredients } = useDebouncedIngredientSearch(
    ingredientSearchQuery,
    {
      delay: 300,
      searchType: 'basic',
      source: searchSource,
      enabled: shouldEnableIngredientSearch,
    }
  );
  const ingredientSearchResults = useSpoonacularTagEnrichment(
    rawIngredientSearchResults as Array<Record<string, unknown>>,
    { enabled: showIngredientResults && !isSearchingIngredients },
  ) as typeof rawIngredientSearchResults;

  const setIngredientSearchQuery = useCallback((value: string) => {
    setIngredientSearchQueryState(value);
    const normalizedValue = value.trim().toLowerCase();
    const normalizedSelected = selectedIngredientName.trim().toLowerCase();
    const matchesLockedSelection = Boolean(normalizedSelected) && normalizedValue === normalizedSelected;
    setIsIngredientSelectionLocked(matchesLockedSelection);

    if (normalizedSelected && normalizedValue !== normalizedSelected) {
      setSelectedIngredientName('');
      setSelectedIngredientLookupId('');
      setSelectedYieldFactor(null);
      setSelectedRetentionFactor(null);
    }
  }, [selectedIngredientName]);

  // Handle manual close from SearchableDropdown
  const handleCloseIngredientDropdown = useCallback(() => {
    ingredientDropdownManuallyClosedRef.current = true;
    setShowIngredientResults(false);
  }, []);

  const invalidateIngredientRequests = useCallback(() => {
    ingredientRequestIdRef.current += 1;
  }, []);

  const resetIngredientForm = useCallback(() => {
    invalidateIngredientRequests();
    setIngredientSearchQueryState('');
    setSelectedIngredientName('');
    setSelectedIngredientLookupId('');
    setIngredientAmount('');
    setIngredientUnit('');
    setUnitWeightGrams('');
    setUnitWeightMl('');
    setBaseNutritionData(null);
    setBaseNutritionForAmount(null);
    setBaseAmountInGrams(null);
    setSelectedYieldFactor(null);
    setSelectedRetentionFactor(null);
    setGramsPerMl(null);
    setAvailableUnits([]);
    setUnitLabelsByKey({});
    setUnitWeightsByKey({});
    setUnitMlWeightsByKey({});
    setShowIngredientResults(false);
    ingredientDropdownManuallyClosedRef.current = false;
    lastFetchedRef.current = null;
    setIsLoadingUnits(false);
    setIsLoadingNutrition(false);
    setIsIngredientSelectionLocked(false);
    selectedIngredientImageUrlRef.current = undefined;
    selectedIngredientAllergensRef.current = [];
    selectedIngredientIntolerancesRef.current = [];
    setSelectedIngredientPreviewUrl(undefined);
  }, [invalidateIngredientRequests]);

  const startAddIngredient = useCallback(() => {
    resetIngredientForm();
    setEditingIngredientId(null);
    setIsAddingIngredient(true);
  }, [resetIngredientForm]);

  const closeIngredientForm = useCallback(() => {
    resetIngredientForm();
    setEditingIngredientId(null);
    setIsAddingIngredient(false);
  }, [resetIngredientForm]);

  // Auto-show dropdown when results arrive
  useEffect(() => {
    if (ingredientDropdownManuallyClosedRef.current) {
      return;
    }

    if (selectedIngredientName && ingredientSearchQuery === selectedIngredientName) {
      setShowIngredientResults(false);
      return;
    }

    if (ingredientSearchQuery.trim() && (isSearchingIngredients || ingredientSearchResults.length > 0)) {
      setShowIngredientResults(true);
    } else if (!ingredientSearchQuery.trim()) {
      setShowIngredientResults(false);
    }
  }, [ingredientSearchResults, ingredientSearchQuery, isSearchingIngredients, selectedIngredientName]);

  // Reset manual close flag when user starts typing
  useEffect(() => {
    if (ingredientDropdownManuallyClosedRef.current && ingredientSearchQuery.trim() !== '') {
      const timeoutId = setTimeout(() => {
        ingredientDropdownManuallyClosedRef.current = false;
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [ingredientSearchQuery]);

  // Auto-fetch nutrition when all required fields are present
  useEffect(() => {
    if (
      selectedIngredientName &&
      ingredientAmount &&
      ingredientUnit &&
      !isLoadingNutrition &&
      !isLoadingUnits
    ) {
      const numQuantity = parseFloat(ingredientAmount);
      if (!Number.isNaN(numQuantity) && numQuantity > 0) {
        const lastFetched = lastFetchedRef.current;
        const needsFetch = !lastFetched
          || lastFetched.ingredientId !== selectedIngredientLookupId
          || lastFetched.ingredient !== selectedIngredientName
          || lastFetched.amount !== numQuantity
          || lastFetched.unit !== ingredientUnit;

        if (needsFetch) {
          lastFetchedRef.current = {
            ingredientId: selectedIngredientLookupId,
            ingredient: selectedIngredientName,
            amount: numQuantity,
            unit: ingredientUnit,
          };
          fetchNutritionData(selectedIngredientName, numQuantity, ingredientUnit, selectedIngredientLookupId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIngredientLookupId, selectedIngredientName, ingredientAmount, ingredientUnit, isLoadingNutrition, isLoadingUnits]);

  const fetchNutritionData = async (
    ingredient: string,
    amount: number,
    unitParam: string,
    ingredientIdParam?: string,
  ) => {
    const requestId = ingredientRequestIdRef.current + 1;
    ingredientRequestIdRef.current = requestId;
    setIsLoadingNutrition(true);
    setBaseNutritionData(null);

    try {
      const normalizedIngredientId = String(ingredientIdParam ?? '').trim();
      lastFetchedRef.current = {
        ingredientId: normalizedIngredientId,
        ingredient,
        amount,
        unit: unitParam,
      };
      const response = normalizedIngredientId
        ? await getIngredientNutritionByIngredientId(normalizedIngredientId, amount, unitParam)
        : await getIngredientNutrition(
            ingredient,
            lookupSource,
            amount,
            unitParam,
          );
      if (requestId !== ingredientRequestIdRef.current) {
        return;
      }

      if (response.success) {
        selectedIngredientAllergensRef.current = mergeRecipeAllergens(
          selectedIngredientAllergensRef.current,
          (response as any).allergenTagIds,
          (response as any).allergens,
        );
        selectedIngredientIntolerancesRef.current = mergeRecipeIntolerances(
          selectedIngredientIntolerancesRef.current,
          (response as any).intoleranceTagIds,
          (response as any).intoleranceTags,
        );
        const {
          units: unitsFromResponse,
          labelsByKey: nextUnitLabelsByKey,
          weightsByKey: nextUnitWeightsByKey,
          mlWeightsByKey: nextUnitMlWeightsByKey,
          gramsPerMl: resolvedGramsPerMl,
        } = extractIngredientUnitMetadata(response);
        setUnitLabelsByKey(nextUnitLabelsByKey);
        if (unitsFromResponse.length > 0) {
          setAvailableUnits(unitsFromResponse);
          if (!unitsFromResponse.some((unit) => unit.toLowerCase() === unitParam.toLowerCase())) {
            setIngredientUnit(unitsFromResponse[0]);
          }
        }

        setUnitWeightsByKey(nextUnitWeightsByKey);
        setUnitMlWeightsByKey(nextUnitMlWeightsByKey);
        setGramsPerMl(resolvedGramsPerMl);

        const currentUnitWeightGrams = toPositiveNumber(unitWeightGrams);
        const standardUnitWeight = toPositiveNumber(nextUnitWeightsByKey[normalizeIngredientUnitKey(unitParam)]);
        const resolvedUnitWeightGrams = currentUnitWeightGrams ?? standardUnitWeight;
        if (currentUnitWeightGrams == null && standardUnitWeight != null && !isGramUnit(unitParam)) {
          setUnitWeightGrams(String(roundTo2(standardUnitWeight)));
        }
        if (!isGramUnit(unitParam)) {
          const mlValue = resolveIngredientUnitMlWeight({
            unit: unitParam,
            unitWeightGrams: resolvedUnitWeightGrams,
            gramsPerMl: resolvedGramsPerMl,
            mlWeightsByKey: nextUnitMlWeightsByKey,
          });
          setUnitWeightMl(mlValue != null ? String(mlValue) : '');
        }

        const responseAmountInGrams = toPositiveNumber((response as any)?.amountInGrams ?? (response as any)?.servingGrams ?? (response as any)?.amount_grams);
        setSelectedYieldFactor(getPositiveFactorOrNull((response as any)?.yieldFactor ?? (response as any)?.yield_factor));
        setSelectedRetentionFactor(getPositiveFactorOrNull((response as any)?.retentionFactor ?? (response as any)?.retention_factor));
        const fallbackAmountInGrams = (
          isGramUnit(unitParam)
            ? amount
            : toPositiveNumber(nextUnitWeightsByKey[normalizeIngredientUnitKey(unitParam)])
              ? amount * Number(nextUnitWeightsByKey[normalizeIngredientUnitKey(unitParam)])
              : null
        );
        const resolvedBaseAmount = responseAmountInGrams ?? (fallbackAmountInGrams ? roundTo2(fallbackAmountInGrams) : null);
        setBaseAmountInGrams(resolvedBaseAmount);

        if (response.nutritionForAmount) setBaseNutritionForAmount(response.nutritionForAmount);

        if (response.nutrition) {
          const mappedData: Ingredient['nutritionData'] = {
            calories: response.nutrition.calories || 0,
            macros: {
              protein: response.nutrition.macros?.protein || 0,
              carbs: response.nutrition.macros?.carbs || 0,
              fat: response.nutrition.macros?.fat || 0,
              fiber: response.nutrition.macros?.fiber || 0,
              saturatedFat: response.nutrition.macros?.saturatedFat || 0,
              alcohol: (response.nutrition.macros as { alcohol?: number } | undefined)?.alcohol ?? 0,
            },
          };
          setBaseNutritionData(mappedData);
        }

        if (response.nutritionForAmount == null) {
          setBaseNutritionForAmount(null);
        }
      } else {
        setBaseNutritionData(null);
        setBaseNutritionForAmount(null);
        setBaseAmountInGrams(null);
        setSelectedYieldFactor(null);
        setSelectedRetentionFactor(null);
        setGramsPerMl(null);
        setUnitLabelsByKey({});
        setUnitWeightsByKey({});
        setUnitMlWeightsByKey({});
      }
    } catch (error) {
      if (requestId !== ingredientRequestIdRef.current) {
        return;
      }
      console.error('Error fetching nutrition data:', error);
      setBaseNutritionData(null);
      setBaseNutritionForAmount(null);
      setBaseAmountInGrams(null);
      setSelectedYieldFactor(null);
      setSelectedRetentionFactor(null);
      setGramsPerMl(null);
      setUnitLabelsByKey({});
      setUnitWeightsByKey({});
      setUnitMlWeightsByKey({});
      showErrorToast('Failed to fetch nutrition data. You can still add the ingredient.', 'Error');
    } finally {
      if (requestId === ingredientRequestIdRef.current) {
        setIsLoadingNutrition(false);
      }
    }
  };

  const handleSelectIngredient = async (item: any) => {
    const requestId = ingredientRequestIdRef.current + 1;
    ingredientRequestIdRef.current = requestId;
    const ingredientName = String(item?.name ?? '').trim();
    const ingredientId = String(item?.id ?? '').trim();
    if (!ingredientName) return;

    selectedIngredientAllergensRef.current = mergeRecipeAllergens(item?.allergenTagIds, item?.allergens);
    selectedIngredientIntolerancesRef.current = mergeRecipeIntolerances(item?.intoleranceTagIds, item?.intoleranceTags);
    setSelectedIngredientName(ingredientName);
    setSelectedIngredientLookupId(ingredientId);
    setSelectedYieldFactor(getPositiveFactorOrNull(item?.yieldFactor ?? item?.yield_factor));
    setSelectedRetentionFactor(getPositiveFactorOrNull(item?.retentionFactor ?? item?.retention_factor));
    const thumb =
      typeof item?.imageUrl === 'string' && item.imageUrl.trim().length > 0
        ? item.imageUrl.trim()
        : typeof item?.image === 'string' && item.image.trim().length > 0
          ? item.image.trim()
          : undefined;
    selectedIngredientImageUrlRef.current = thumb;
    setSelectedIngredientPreviewUrl(thumb);
    setIngredientSearchQueryState(ingredientName);
    setIsIngredientSelectionLocked(true);
    setShowIngredientResults(false);

    setIsLoadingUnits(true);
    setAvailableUnits([]);
    setUnitLabelsByKey({});
    setUnitWeightsByKey({});
    setUnitMlWeightsByKey({});
    setIngredientUnit('');
    setUnitWeightGrams('');
    setUnitWeightMl('');
    setGramsPerMl(null);

    try {
      let units: string[] = [];
      let labelsByKey: Record<string, string> = {};
      let unitWeights: Record<string, number> = {};
      let unitMlWeights: Record<string, number> = {};
      let nextGramsPerMl: number | null = null;

      const nutritionResponse = ingredientId
        ? await getIngredientNutritionByIngredientId(ingredientId)
        : await getIngredientNutrition(
            ingredientName,
            lookupSource,
            1,
            '',
          );
      if (nutritionResponse.success) {
        selectedIngredientAllergensRef.current = mergeRecipeAllergens(
          selectedIngredientAllergensRef.current,
          (nutritionResponse as any).allergenTagIds,
          (nutritionResponse as any).allergens,
        );
        selectedIngredientIntolerancesRef.current = mergeRecipeIntolerances(
          selectedIngredientIntolerancesRef.current,
          (nutritionResponse as any).intoleranceTagIds,
          (nutritionResponse as any).intoleranceTags,
        );
        const metadata = extractIngredientUnitMetadata(nutritionResponse);
        if (requestId !== ingredientRequestIdRef.current) {
          return;
        }
        units = metadata.units;
        labelsByKey = metadata.labelsByKey;
        unitWeights = metadata.weightsByKey;
        unitMlWeights = metadata.mlWeightsByKey;
        nextGramsPerMl = metadata.gramsPerMl;
        setGramsPerMl(nextGramsPerMl);
        setSelectedYieldFactor(getPositiveFactorOrNull((nutritionResponse as any)?.yieldFactor ?? (nutritionResponse as any)?.yield_factor) ?? getPositiveFactorOrNull(item?.yieldFactor ?? item?.yield_factor));
        setSelectedRetentionFactor(getPositiveFactorOrNull((nutritionResponse as any)?.retentionFactor ?? (nutritionResponse as any)?.retention_factor) ?? getPositiveFactorOrNull(item?.retentionFactor ?? item?.retention_factor));
      }

      if (units.length === 0) {
        units = ['g'];
      }
      if (requestId !== ingredientRequestIdRef.current) {
        return;
      }

      setAvailableUnits(units);
      setUnitLabelsByKey(labelsByKey);
      setUnitWeightsByKey(unitWeights);
      setUnitMlWeightsByKey(unitMlWeights);

      if (units.length > 0) {
        const preferredUnit = String((nutritionResponse as any)?.unit ?? '').trim();
        const selected = preferredUnit && units.some((u) => u.toLowerCase() === preferredUnit.toLowerCase())
          ? units.find((u) => u.toLowerCase() === preferredUnit.toLowerCase()) || units[0]
          : units[0];
        setIngredientUnit(selected);
        const standardUnitWeight = toPositiveNumber(unitWeights[normalizeIngredientUnitKey(selected)]);
        if (isGramUnit(selected)) {
          setUnitWeightGrams('');
          setUnitWeightMl('');
        } else {
          setUnitWeightGrams(standardUnitWeight ? String(roundTo2(standardUnitWeight)) : '');
          const mlValue = resolveIngredientUnitMlWeight({
            unit: selected,
            unitWeightGrams: standardUnitWeight,
            gramsPerMl: nextGramsPerMl,
            mlWeightsByKey: unitMlWeights,
          });
          setUnitWeightMl(mlValue != null ? String(mlValue) : '');
        }
      }
    } catch (error) {
      if (requestId !== ingredientRequestIdRef.current) {
        return;
      }
      console.error('Error fetching units:', error);
      showErrorToast('Failed to load units for this ingredient. Please try again.', 'Error');
    } finally {
      if (requestId === ingredientRequestIdRef.current) {
        setIsLoadingUnits(false);
      }
    }
  };

  const handleUnitChange = (newUnit: string) => {
    setIngredientUnit(newUnit);
    const standardUnitWeight = toPositiveNumber(unitWeightsByKey[normalizeIngredientUnitKey(newUnit)]);
    if (isGramUnit(newUnit)) {
      setUnitWeightGrams('');
      setUnitWeightMl('');
    } else {
      setUnitWeightGrams(standardUnitWeight ? String(roundTo2(standardUnitWeight)) : '');
      const mlValue = resolveIngredientUnitMlWeight({
        unit: newUnit,
        unitWeightGrams: standardUnitWeight,
        gramsPerMl: effectiveGramsPerMl,
        mlWeightsByKey: unitMlWeightsByKey,
      });
      setUnitWeightMl(mlValue != null ? String(mlValue) : '');
    }
    setBaseNutritionData(null);
    setBaseNutritionForAmount(null);
    setBaseAmountInGrams(null);
    lastFetchedRef.current = null;
  };

  const handleUnitWeightChange = (value: string) => {
    setUnitWeightGrams(value);
    const grams = toPositiveNumber(value);
    const mlValue = gramsToMl(grams, effectiveGramsPerMl);
    setUnitWeightMl(mlValue != null ? String(mlValue) : '');
  };

  const handleUnitWeightMlChange = (value: string) => {
    setUnitWeightMl(value);
    const ml = toPositiveNumber(value);
    const gramsValue = mlToGrams(ml, effectiveGramsPerMl);
    setUnitWeightGrams(gramsValue != null ? String(gramsValue) : '');
  };

  const handleQuantityChange = (newQuantity: string) => {
    setIngredientAmount(newQuantity);
    if (newQuantity === '' || parseFloat(newQuantity) <= 0) {
      setBaseNutritionData(null);
      setBaseNutritionForAmount(null);
      setBaseAmountInGrams(null);
      lastFetchedRef.current = null;
    }
  };

  const handleEditIngredient = (ingredient: Ingredient) => {
    const requestId = ingredientRequestIdRef.current + 1;
    ingredientRequestIdRef.current = requestId;
    const ingredientLookupId = String(ingredient.ingredientId ?? '').trim();
    setIsAddingIngredient(true);
    setEditingIngredientId(ingredient.id);
    selectedIngredientImageUrlRef.current = ingredient.imageUrl;
    selectedIngredientAllergensRef.current = mergeRecipeAllergens(ingredient.allergenTagIds, ingredient.allergens);
    selectedIngredientIntolerancesRef.current = mergeRecipeIntolerances(ingredient.intoleranceTagIds, ingredient.intoleranceTags);
    setSelectedIngredientPreviewUrl(ingredient.imageUrl);
    setIngredientSearchQueryState(ingredient.name);
    setSelectedIngredientName(ingredient.name);
    setSelectedIngredientLookupId(ingredientLookupId);
    setIsIngredientSelectionLocked(true);
    setIngredientAmount(ingredient.amount.toString());
    setIngredientUnit(ingredient.unit);
    const initialDensity = toPositiveNumber(ingredient.gramsPerMl) ?? gramsPerMl;
    const initialUnitWeightGrams = resolveUnitWeightFromIngredient(ingredient);
    setUnitWeightGrams(isGramUnit(ingredient.unit) ? '' : (initialUnitWeightGrams?.toString() ?? ''));
    const initialUnitWeightMl = resolveUnitWeightMlFromIngredient(ingredient, initialDensity);
    setUnitWeightMl(isGramUnit(ingredient.unit) ? '' : (initialUnitWeightMl?.toString() ?? ''));
    setBaseNutritionData(ingredient.nutritionData || null);
    setBaseNutritionForAmount(ingredient.nutrition ?? null);
    setBaseAmountInGrams(toPositiveNumber(ingredient.amountGrams));
    setSelectedYieldFactor(getPositiveFactorOrNull(ingredient.yieldFactor));
    setSelectedRetentionFactor(getPositiveFactorOrNull(ingredient.retentionFactor));
    setAvailableUnits(ingredient.unit ? [ingredient.unit] : []);
    setUnitLabelsByKey({});
    setUnitWeightsByKey({});
    setUnitMlWeightsByKey({});
    setShowIngredientResults(false);
    ingredientDropdownManuallyClosedRef.current = true;
    lastFetchedRef.current = {
      ingredientId: ingredientLookupId,
      ingredient: ingredient.name,
      amount: ingredient.amount,
      unit: ingredient.unit,
    };

    setIsLoadingUnits(true);
    const effectiveName = ingredient.name?.trim();
    if (!effectiveName) {
      setAvailableUnits(ingredient.unit ? [ingredient.unit] : []);
      setUnitLabelsByKey({});
      setUnitWeightsByKey({});
      setUnitMlWeightsByKey({});
      setIsLoadingUnits(false);
      return;
    }

    (
      ingredientLookupId
        ? getIngredientNutritionByIngredientId(
            ingredientLookupId,
            Number.isFinite(Number(ingredient.amount)) ? Number(ingredient.amount) : 1,
            ingredient.unit || '',
          )
        : getIngredientNutrition(
            effectiveName,
            lookupSource,
            Number.isFinite(Number(ingredient.amount)) ? Number(ingredient.amount) : 1,
            ingredient.unit || '',
          )
    )
      .then((nutritionResponse) => {
        if (requestId !== ingredientRequestIdRef.current) {
          return;
        }
        const metadata = extractIngredientUnitMetadata(nutritionResponse);
        selectedIngredientAllergensRef.current = mergeRecipeAllergens(
          selectedIngredientAllergensRef.current,
          (nutritionResponse as any).allergenTagIds,
          (nutritionResponse as any).allergens,
        );
        selectedIngredientIntolerancesRef.current = mergeRecipeIntolerances(
          selectedIngredientIntolerancesRef.current,
          (nutritionResponse as any).intoleranceTagIds,
          (nutritionResponse as any).intoleranceTags,
        );
        const mergedUnits = normalizeIngredientUnits([ingredient.unit, ...metadata.units]);
        setAvailableUnits(mergedUnits.length > 0 ? mergedUnits : ingredient.unit ? [ingredient.unit] : []);
        setUnitLabelsByKey(metadata.labelsByKey);
        setUnitWeightsByKey(metadata.weightsByKey);
        setUnitMlWeightsByKey(metadata.mlWeightsByKey);
        const nextGramsPerMl = metadata.gramsPerMl;
        const savedDensity = toPositiveNumber(ingredient.gramsPerMl);
        const conversionDensity = savedDensity ?? nextGramsPerMl;
        setGramsPerMl(nextGramsPerMl);
        setSelectedYieldFactor(getPositiveFactorOrNull((nutritionResponse as any)?.yieldFactor ?? (nutritionResponse as any)?.yield_factor) ?? getPositiveFactorOrNull(ingredient.yieldFactor));
        setSelectedRetentionFactor(getPositiveFactorOrNull((nutritionResponse as any)?.retentionFactor ?? (nutritionResponse as any)?.retention_factor) ?? getPositiveFactorOrNull(ingredient.retentionFactor));
        if (!toPositiveNumber(ingredient.unitWeightGrams)) {
          const standardWeight = toPositiveNumber(metadata.weightsByKey[normalizeIngredientUnitKey(ingredient.unit)]);
          if (standardWeight) {
            setUnitWeightGrams(isGramUnit(ingredient.unit) ? '' : String(roundTo2(standardWeight)));
            const mlValue = resolveIngredientUnitMlWeight({
              unit: ingredient.unit,
              unitWeightGrams: standardWeight,
              gramsPerMl: conversionDensity,
              mlWeightsByKey: metadata.mlWeightsByKey,
            });
            setUnitWeightMl(mlValue != null ? String(mlValue) : '');
          }
        } else {
          const explicitWeight = toPositiveNumber(ingredient.unitWeightGrams);
          const mlValue = resolveIngredientUnitMlWeight({
            unit: ingredient.unit,
            unitWeightMl: ingredient.unitWeightMl,
            unitWeightGrams: explicitWeight,
            gramsPerMl: conversionDensity,
            mlWeightsByKey: metadata.mlWeightsByKey,
          });
          setUnitWeightMl(mlValue != null ? String(mlValue) : '');
        }
      })
      .catch((error) => {
        if (requestId !== ingredientRequestIdRef.current) {
          return;
        }
        console.error('Error fetching ingredient nutrition for edit:', error);
        setAvailableUnits(ingredient.unit ? [ingredient.unit] : []);
        setUnitLabelsByKey({});
        setUnitWeightsByKey({});
        setUnitMlWeightsByKey({});
        setGramsPerMl(null);
      })
      .finally(() => {
        if (requestId === ingredientRequestIdRef.current) {
          setIsLoadingUnits(false);
        }
      });
  };

  const handleSubmitIngredient = (options?: { continueAdding?: boolean }) => {
    const trimmedName = selectedIngredientName.trim();

    if (!trimmedName || !ingredientAmount || !ingredientUnit) {
      showWarningToast('Missing Information', 'Please fill in ingredient name, amount, and unit.');
      return;
    }

    const parsedAmount = parseFloat(ingredientAmount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      showWarningToast('Invalid Quantity', 'Please enter a valid amount greater than zero.');
      return;
    }

    const defaultNutrition: Ingredient['nutritionData'] = {
      calories: 0,
      macros: {
        protein: 0,
        carbs: 0,
        fat: 0,
        fiber: 0,
        saturatedFat: 0,
        alcohol: 0,
      },
    };

    const nutritionPayload = effectiveNutritionData ?? (editingIngredientId ? undefined : defaultNutrition);
    const totalAmountGrams = effectiveAmountGrams;

    if (editingIngredientId) {
      setIngredients((prev) =>
        prev.map((ingredient) =>
          ingredient.id === editingIngredientId
            ? {
                ...ingredient,
                imageUrl: selectedIngredientImageUrlRef.current ?? ingredient.imageUrl,
                ingredientId: selectedIngredientLookupId || ingredient.ingredientId,
                name: trimmedName,
                amount: parsedAmount,
                unit: ingredientUnit,
                amountGrams: totalAmountGrams ?? ingredient.amountGrams,
                amountMl: effectiveAmountMl ?? ingredient.amountMl,
                unitWeightGrams: isGramUnit(ingredientUnit)
                  ? undefined
                  : (parsedUnitWeightOverrideGrams ?? ingredient.unitWeightGrams),
                unitWeightMl: isGramUnit(ingredientUnit)
                  ? undefined
                  : (parsedUnitWeightOverrideMl ?? ingredient.unitWeightMl),
                gramsPerMl: ingredient.gramsPerMl,
                yieldFactor: selectedYieldFactor ?? ingredient.yieldFactor ?? undefined,
                retentionFactor: selectedRetentionFactor ?? ingredient.retentionFactor ?? undefined,
                allergens: selectedIngredientAllergensRef.current,
                allergenTagIds: selectedIngredientAllergensRef.current,
                intoleranceTags: selectedIngredientIntolerancesRef.current,
                intoleranceTagIds: selectedIngredientIntolerancesRef.current,
                nutritionData: nutritionPayload ?? ingredient.nutritionData,
                nutrition: effectiveNutritionForAmount ?? ingredient.nutrition,
              }
            : ingredient,
        ),
      );
    } else {
      const newIngredient: Ingredient = {
        id: `ingredient-${Date.now()}-${Math.random()}`,
        ...(selectedIngredientImageUrlRef.current
          ? { imageUrl: selectedIngredientImageUrlRef.current }
          : {}),
        ...(selectedIngredientLookupId && { ingredientId: selectedIngredientLookupId }),
        name: trimmedName,
        amount: parsedAmount,
        unit: ingredientUnit,
        amountGrams: totalAmountGrams ?? undefined,
        amountMl: effectiveAmountMl ?? undefined,
        unitWeightGrams: isGramUnit(ingredientUnit) ? undefined : (parsedUnitWeightOverrideGrams ?? undefined),
        unitWeightMl: isGramUnit(ingredientUnit) ? undefined : (parsedUnitWeightOverrideMl ?? undefined),
        gramsPerMl,
        yieldFactor: selectedYieldFactor ?? undefined,
        retentionFactor: selectedRetentionFactor ?? undefined,
        allergens: selectedIngredientAllergensRef.current,
        allergenTagIds: selectedIngredientAllergensRef.current,
        intoleranceTags: selectedIngredientIntolerancesRef.current,
        intoleranceTagIds: selectedIngredientIntolerancesRef.current,
        nutritionData: nutritionPayload,
        nutrition: effectiveNutritionForAmount ?? undefined,
      };

      setIngredients((prev) => [...prev, newIngredient]);
    }

    if (options?.continueAdding) {
      resetIngredientForm();
      setEditingIngredientId(null);
      setIsAddingIngredient(true);
    } else {
      closeIngredientForm();
    }
  };

  const handleRemoveIngredient = (id: string) => {
    setIngredients((prev) => prev.filter((ing) => ing.id !== id));
    if (editingIngredientId === id) {
      closeIngredientForm();
    }
  };

  const handleBulkAddIngredients = useCallback(async () => {
    const names = bulkIngredientInput
      .split(/[,\n]+/)
      .map((name) => name.trim())
      .filter(Boolean);

    if (names.length === 0) {
      showWarningToast('No Ingredients', 'Enter ingredients separated by commas or new lines.');
      return;
    }

    setIsBulkAddingIngredients(true);
    const added: Ingredient[] = [];
    const skipped: string[] = [];

    for (const name of names) {
      try {
        const autocomplete = await getIngredientsAutocomplete(name, 'basic', undefined, searchSource);
        const matchedItem = autocomplete.items?.[0];
        const matchedName = String(matchedItem?.name ?? name).trim();
        const matchedId = String(matchedItem?.id ?? '').trim();
        const detailsResponse = matchedId
          ? await getIngredientNutritionByIngredientId(matchedId)
          : await getIngredientNutrition(matchedName, lookupSource, 1, '');
        const metadata = extractIngredientUnitMetadata(detailsResponse);
        const preferredUnit = String((detailsResponse as any)?.unit ?? '').trim();
        const units = metadata.units.length > 0 ? metadata.units : normalizeIngredientUnits(matchedItem?.possibleUnits ?? ['g']);
        const unit = (
          preferredUnit && units.some((candidate) => candidate.toLowerCase() === preferredUnit.toLowerCase())
            ? units.find((candidate) => candidate.toLowerCase() === preferredUnit.toLowerCase())
            : units[0]
        ) || 'g';
        const amount = 1;
        const nutritionResponse = matchedId
          ? await getIngredientNutritionByIngredientId(matchedId, amount, unit)
          : await getIngredientNutrition(matchedName, lookupSource, amount, unit);
        const nutritionMetadata = extractIngredientUnitMetadata(nutritionResponse);
        const effectiveMetadata = nutritionMetadata.units.length > 0 ? nutritionMetadata : metadata;
        const unitWeight = isGramUnit(unit)
          ? null
          : toPositiveNumber(effectiveMetadata.weightsByKey[normalizeIngredientUnitKey(unit)]);
        const amountGrams = isGramUnit(unit)
          ? amount
          : unitWeight
            ? roundTo2(amount * unitWeight)
            : toPositiveNumber((nutritionResponse as any)?.amountInGrams ?? (nutritionResponse as any)?.servingGrams);
        const unitMlWeight = isGramUnit(unit)
          ? null
          : resolveIngredientUnitMlWeight({
              unit,
              unitWeightGrams: unitWeight,
              gramsPerMl: effectiveMetadata.gramsPerMl,
              mlWeightsByKey: effectiveMetadata.mlWeightsByKey,
            });
        const amountMl = toPositiveNumber((nutritionResponse as any)?.amountInMl ?? (nutritionResponse as any)?.servingMl)
          ?? (unitMlWeight ? roundTo2(amount * unitMlWeight) : null);
        const nutritionForAmount = (nutritionResponse as any)?.nutritionForAmount ?? null;
        const allergens = mergeRecipeAllergens(
          matchedItem?.allergenTagIds,
          matchedItem?.allergens,
          (detailsResponse as any)?.allergenTagIds,
          (detailsResponse as any)?.allergens,
          (nutritionResponse as any)?.allergenTagIds,
          (nutritionResponse as any)?.allergens,
        );
        const intolerances = mergeRecipeIntolerances(
          matchedItem?.intoleranceTagIds,
          matchedItem?.intoleranceTags,
          (detailsResponse as any)?.intoleranceTagIds,
          (detailsResponse as any)?.intoleranceTags,
          (nutritionResponse as any)?.intoleranceTagIds,
          (nutritionResponse as any)?.intoleranceTags,
        );
        const nutritionData = nutritionForAmountToNutritionData(nutritionForAmount)
          ?? ((nutritionResponse as any)?.nutrition
            ? {
                calories: Number((nutritionResponse as any).nutrition.calories) || 0,
                macros: {
                  protein: Number((nutritionResponse as any).nutrition.macros?.protein) || 0,
                  carbs: Number((nutritionResponse as any).nutrition.macros?.carbs) || 0,
                  fat: Number((nutritionResponse as any).nutrition.macros?.fat) || 0,
                  fiber: Number((nutritionResponse as any).nutrition.macros?.fiber) || 0,
                  saturatedFat: Number((nutritionResponse as any).nutrition.macros?.saturatedFat) || 0,
                  alcohol: Number((nutritionResponse as any).nutrition.macros?.alcohol) || 0,
                },
              }
            : undefined);
        const imageUrl =
          typeof matchedItem?.imageUrl === 'string' && matchedItem.imageUrl.trim()
            ? matchedItem.imageUrl.trim()
            : typeof matchedItem?.image === 'string' && matchedItem.image.trim()
              ? matchedItem.image.trim()
              : undefined;

        added.push({
          id: `ingredient-${Date.now()}-${Math.random()}`,
          ...(matchedId ? { ingredientId: matchedId } : {}),
          ...(imageUrl ? { imageUrl } : {}),
          name: matchedName || name,
          amount,
          unit,
          ...(amountGrams ? { amountGrams } : {}),
          ...(unitWeight ? { unitWeightGrams: unitWeight } : {}),
          ...(unitMlWeight ? { unitWeightMl: unitMlWeight } : {}),
          ...(amountMl ? { amountMl } : {}),
          ...(effectiveMetadata.gramsPerMl ? { gramsPerMl: effectiveMetadata.gramsPerMl } : {}),
          yieldFactor: getPositiveFactorOrNull((nutritionResponse as any)?.yieldFactor ?? (nutritionResponse as any)?.yield_factor) ?? undefined,
          retentionFactor: getPositiveFactorOrNull((nutritionResponse as any)?.retentionFactor ?? (nutritionResponse as any)?.retention_factor) ?? undefined,
          allergens,
          allergenTagIds: allergens,
          intoleranceTags: intolerances,
          intoleranceTagIds: intolerances,
          nutritionData,
          nutrition: nutritionForAmount ?? undefined,
        });
      } catch (error) {
        console.error('Error bulk adding ingredient:', name, error);
        skipped.push(name);
      }
    }

    if (added.length > 0) {
      setIngredients((prev) => [...prev, ...added]);
      setBulkIngredientInput('');
      closeIngredientForm();
      showSuccessToast('Ingredients Added', `Added ${added.length} ingredient${added.length === 1 ? '' : 's'} with default quantities.`);
    }

    if (skipped.length > 0) {
      showWarningToast('Some Ingredients Skipped', `Could not add: ${skipped.join(', ')}`);
    }

    setIsBulkAddingIngredients(false);
  }, [bulkIngredientInput, closeIngredientForm, lookupSource, searchSource]);

  // Calculate total macros from all ingredients
  const calculateTotalMacros = useCallback(() => {
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      saturatedFat: 0,
      alcohol: 0,
    };

    ingredients.forEach((ingredient) => {
      if (ingredient.nutritionData) {
        totals.calories += ingredient.nutritionData.calories || 0;
        totals.protein += ingredient.nutritionData.macros?.protein || 0;
        totals.carbs += ingredient.nutritionData.macros?.carbs || 0;
        totals.fat += ingredient.nutritionData.macros?.fat || 0;
        totals.fiber += ingredient.nutritionData.macros?.fiber || 0;
        totals.saturatedFat += ingredient.nutritionData.macros?.saturatedFat || 0;
        let alcoholG = ingredient.nutritionData.macros?.alcohol ?? 0;
        if ((!alcoholG || alcoholG === 0) && ingredient.nutrition) {
          const flat = ingredient.nutrition as Record<string, number>;
          alcoholG = Number(flat.alcohol ?? flat.ethyl_alcohol) || 0;
        }
        totals.alcohol += alcoholG;
      }
    });

    return totals;
  }, [ingredients]);

  const totalMacros = calculateTotalMacros();

  return {
    // State
    ingredients,
    isAddingIngredient,
    ingredientSearchQuery,
    selectedIngredientName,
    selectedIngredientLookupId,
    selectedIngredientPreviewUrl,
    selectedYieldFactor,
    selectedRetentionFactor,
    ingredientAmount,
    ingredientUnit,
    unitWeightGrams,
    unitWeightMl,
    effectiveAmountGrams,
    effectiveAmountMl,
    gramsPerMl,
    availableUnits,
    unitLabelsByKey,
    unitWeightsByKey,
    nutritionData: effectiveNutritionData,
    showIngredientResults,
    ingredientSearchResults,
    isSearchingIngredients,
    isLoadingNutrition,
    bulkIngredientInput,
    isBulkAddingIngredients,
    isLoadingUnits,
    editingIngredientId,
    totalMacros,
    // Actions
    setIngredients,
    setIngredientSearchQuery,
    startAddIngredient,
    closeIngredientForm,
    handleSelectIngredient,
    handleQuantityChange,
    handleUnitChange,
    handleUnitWeightChange,
    handleUnitWeightMlChange,
    handleCloseIngredientDropdown,
    handleSubmitIngredient,
    handleEditIngredient,
    handleRemoveIngredient,
    setBulkIngredientInput,
    handleBulkAddIngredients,
  };
};
